<template>
  <li
    v-if="blog"
    :class="{
      'group flex border border-www-color-neutral-very-light transition-shadow duration-200 ease-out hover:shadow-card': true,
      'col-span-3 grid grid-cols-10 flex-row': isFeatured,
      'col-span-3 flex-col md:col-span-1': !isFeatured,
    }"
  >
    <PTCLink
      v-if="blog.cardImageSrc || blog.cardImageRef?.asset?._ref"
      :class="{
        'group/more h-52 w-full items-center gap-6 self-start': true,
        'col-span-10 h-full md:col-span-6': isFeatured,
        flex: !isTabletOrMobile,
        hidden: hideImageOnMobile && isTabletOrMobile,
      }"
      :target="isOffSite ? '_blank' : undefined"
      :to="destinationRoute"
    >
      <PtcPicture
        v-if="blog.cardImageRef?.asset?._ref"
        :class="{ 'block h-full w-full': true }"
        provider="sanity"
        format="webp"
        :src="blog.cardImageRef?.asset?._ref"
        :alt="blog?.cardImageAltText || 'Card hero image'"
        loading="lazy"
        :modifiers="{
          w: 720,
        }"
        :img-attrs="{
          class:
            'group-hover:animate-flash min-h-full min-w-full block object-cover object-center h-[203px]',
        }"
      />
      <img
        v-else-if="blog.cardImageSrc"
        :class="{
          'block min-h-full min-w-full object-cover object-center group-hover:animate-flash': true,
        }"
        :src="blog.cardImageSrc"
        alt="Card hero image"
        loading="lazy"
      />
    </PTCLink>

    <div
      :class="{
        'flex flex-1 flex-col gap-3.5 bg-www-color-white px-25px pb-30px pt-25px': true,
        'col-span-10 md:col-span-4': isFeatured,
      }"
    >
      <Taxonomies
        v-if="blog.taxonomies"
        link-type="event"
        :taxonomies="blog.taxonomies"
        @taxonomy-change="emitChange"
      />

      <PTCLink
        :target="isOffSite ? '_blank' : undefined"
        :to="destinationRoute"
      >
        <h3
          :class="{
            'title text-base font-black leading-7 text-www-color-neutral-medium-dark hover:underline group-hover:text-www-color-primary-lighter lg:text-22px lg:leading-8 lg:transition-all lg:duration-200 lg:ease-in-out': true,
          }"
        >
          {{ blog.title }}
        </h3>
      </PTCLink>

      <span
        v-if="blog.articleDate"
        :class="{ 'text-10px text-www-color-neutral-medium-dark': true }"
      >
        {{
          isEvent ?
            formatLocalDateWithZone(blog.articleDate)
          : formatDateUTC(blog.articleDate, 'MM.DD.YYYY')
        }}
      </span>

      <p
        v-if="blog.description"
        :title="blog.description"
        :class="{
          'mb-3 hidden text-sm font-light text-www-color-neutral-medium-dark lg:line-clamp-5': true,
        }"
      >
        {{ blog.description }}
      </p>

      <PTCLink
        :target="isOffSite ? '_blank' : undefined"
        :to="destinationRoute"
        :class="{ 'group/more flex items-center gap-5 self-start': true }"
      >
        <span
          :class="{
            'text-8px font-semibold uppercase leading-none tracking-[.05em] text-www-color-primary-light group-hover/more:underline': true,
          }"
        >
          learn more
        </span>

        <PtcIcon
          name="ptc-blog-card-arrow"
          :class="{
            'text-8px text-www-color-primary-light group-hover/more:text-www-color-primary-lighter': true,
          }"
        />
      </PTCLink>

      <template v-if="blog.person?.reference">
        <div
          v-if="Array.isArray(blog.person)"
          :class="{
            'flex flex-wrap gap-2': true,
          }"
        >
          <Person
            v-for="(author, index) in blog.person"
            :key="index"
            v-bind="author.reference"
            :class="{ 'mt-auto hidden lg:flex': true }"
          />
        </div>

        <Person
          v-else
          v-bind="blog.person.reference"
          :class="{ 'mt-auto hidden lg:flex': true }"
        />
      </template>
    </div>
  </li>
</template>

<script lang="ts" setup>
import type { CardBlogResult } from '~/sanity/types';

const { isTabletOrMobile } = useResponsive().getDefault();

const props = withDefaults(
  defineProps<{
    blog: CardBlogResult;
    hideImageOnMobile?: boolean;
    isFeatured?: boolean;
  }>(),
  {
    hideImageOnMobile: true,
  }
);
const isEvent = ['lumaEvent', 'genericEvent', 'linkedEvent'].includes(props.blog?._type ?? '');
const offSiteUrl = props.blog ? props.blog.offSiteUrl : null;
const isOffSite = !!offSiteUrl;

let destinationRoute: string;
if (isOffSite) {
  destinationRoute = offSiteUrl;
} else if (props.blog && props.blog.currentPathname) {
  destinationRoute = props.blog.currentPathname;
} else {
  console.warn(`No currentPathname found for blog: ${props.blog}`);
  destinationRoute = '#';
}

const emit = defineEmits<{
  (_evt: 'taxonomyChange', _payload: TaxonomyChangedPayload): void;
}>();

const emitChange = (payload: TaxonomyChangedPayload) => {
  emit('taxonomyChange', payload);
};
</script>

<style scoped>
@-webkit-keyframes flash {
  0% {
    opacity: 0.6;
  }
  100% {
    opacity: 1;
  }
}

@keyframes flash {
  0% {
    opacity: 0.6;
  }
  100% {
    opacity: 1;
  }
}
</style>
